import * as React from 'react';
import { CompactDataTable } from './CompactDataTable';
import { ControlBarProps } from './Controls/ControlBar';
import { CountDisplayProps } from './Controls/Count/CountDisplay';
import { FillPopupProps } from './Fill/FillPopup';
import { FullDataTable } from './FullDataTable';
import { ItemsPerPageProps } from './Controls/ItemsPerPage/ItemsPerPage';
import { PaginationProps } from './Controls/Pagination/Pagination';

/**
 * SimpleDataTable's initialization options
 * @param T The type parameter
 */
interface DataTableProps<T extends DataTableData>
    extends DataTableOptions<T>,
        ComponentProps {
    /**
     * Initial filter text
     */
    filterText?: string;
}

interface DataTableState<T extends DataTableData> {
    /**
     * The full set of data
     */
    data: T[];
    /**
     * The subset of filtered data
     */
    filteredData: T[];
    /**
     * Whether we are showing only active records
     */
    showActiveOnly: boolean;
    /**
     * The text to filter the filter column with
     */
    filterText: string;
    /**
     * The current page
     */
    currentPage: number;
    /**
     * The number of items per page
     */
    itemsPerPage: number;
    /**
     * The subset of data displayed on the screen
     */
    displayedData: T[];
    /**
     * The total number of pages
     */
    numPages: number;
    /**
     * The column the data is sorted on
     */
    sortColumn?: Column;
    /**
     * The direction the data is sorted
     */
    sortDirection: SortDirection;
    /**
     * The column that the fill popup is for
     */
    fillColumn?: Column;
    /**
     * Properties for the fill popup
     */
    fillProps: {
        /**
         * Whether the fill popup is displayed
         */
        show: boolean;
        /**
         * The initial x position of the popup
         */
        x: number;
        /**
         * The initial y position of the popup
         */
        y: number;
    };
}

/**
 * The main component, holds the majority of the state
 */
export class DataTable<T extends DataTableData> extends React.Component<
    DataTableProps<T>,
    DataTableState<T>
> {
    constructor(props: DataTableProps<T>) {
        super(props);

        const filterText = props.filterText ? props.filterText : '';
        const showActiveOnly = props.activeFiltering ? true : false;
        const sortDirection = props.initialSortDirection
            ? props.initialSortDirection
            : 'ASCENDING';
        const sortColumn = props.initialSortColumn
            ? props.initialSortColumn
            : undefined;
        const tempData = this.filterData(
            filterText,
            showActiveOnly,
            props.data
        );

        const filteredData = sortColumn
            ? this.sortData(tempData, sortDirection, sortColumn)
            : tempData;
        const itemsPerPage = props.itemsPerPage ? props.itemsPerPage : 15;
        const numPages = this.calculateNumberOfPages(
            filteredData,
            itemsPerPage
        );
        const currentPage = 1;

        const displayedData = this.getItemsForCurrentPage(
            filteredData,
            itemsPerPage,
            currentPage,
            numPages
        );

        this.state = {
            data: [...props.data],
            filteredData,
            showActiveOnly,
            filterText,
            currentPage,
            itemsPerPage,
            displayedData,
            numPages,
            sortDirection,
            sortColumn,
            fillProps: {
                show: false,
                x: 0,
                y: 0
            }
        };
    }

    componentWillReceiveProps(nextProps: DataTableProps<T>) {
        this.processData(undefined, undefined, nextProps.filterText);
    }

    render() {
        const width = this.props.columns.length * 150;
        const style = { width: width + 'px' };

        const countDisplayProps: CountDisplayProps = this.calculateCountDisplay(
            this.state.itemsPerPage,
            this.state.currentPage,
            this.state.filteredData.length
        );

        const itemsPerPageProps: ItemsPerPageProps = {
            itemsPerPage: this.state.itemsPerPage,
            onItemsPerPageChange: (value: number) =>
                this.handleItemsPerPageChange(value)
        };

        const paginationProps: PaginationProps = {
            page: this.state.currentPage,
            numPages: this.state.numPages,
            onPageChange: (page: number) => this.changePage(page)
        };

        const controlBarProps: ControlBarProps = {
            countDisplay: countDisplayProps,
            itemsPerPage: itemsPerPageProps,
            pagination: paginationProps
        };

        const commonProps: CommonDataTableProps<T> = {
            columns: this.props.columns,
            displayedData: this.state.displayedData,
            fillProps: {
                ...this.state.fillProps,
                onClose: (fillAll: boolean, value?: string) =>
                    this.onFill(fillAll, value)
            },
            handleInputColumnChange: (
                itemKey: string,
                column: Column,
                value: string
            ) => this.handleInputColumnChange(itemKey, column, value),
            handleSort: (sortColumn: Column, sortDirection: SortDirection) =>
                this.handleSort(sortColumn, sortDirection),
            idSuffix: this.props.idSuffix,
            showFillPopup: (
                column: Column,
                event: React.MouseEvent<HTMLAnchorElement>
            ) => this.showFillPopup(column, event),
            sortColumn: this.state.sortColumn,
            style,
            sortDirection: this.state.sortDirection
        };

        if (this.props.compactMode) {
            return (
                <CompactDataTable
                    fillProps={{
                        ...this.state.fillProps,
                        onClose: (fillAll: boolean, value?: string) => {
                            this.onFill(fillAll);
                        }
                    }}
                    paginationProps={paginationProps}
                    {...commonProps}
                    style={style}
                />
            );
        } else {
            return (
                <FullDataTable
                    showActiveOnly={this.state.showActiveOnly}
                    activeFiltering={this.props.activeFiltering}
                    controlBarProps={controlBarProps}
                    filterColumn={this.props.filterColumn}
                    filterText={this.state.filterText}
                    onActiveChange={(checked: boolean) =>
                        this.handleActiveChange(checked)
                    }
                    onFilterChange={(filterText: string) =>
                        this.handleFilterSearch(filterText)
                    }
                    {...commonProps}
                />
            );
        }
    }

    /**
     * Returns a copy of the this datatable's data
     */
    getData() {
        return [...this.state.data];
    }

    /**
     * Returns the currently displayed data
     * @author WJF 9-24-18 WIDGET-4
     */
    getDisplayedData() {
        return [...this.state.displayedData];
    }

    /**
     * Handles a search in the filter
     * @param filterText The new filtertext
     */
    private handleFilterSearch(filterText: string) {
        this.processData(
            undefined,
            undefined,
            filterText,
            undefined,
            undefined,
            undefined,
            undefined
        );
    }

    /**
     * Handles toggling show active only
     * @param showActiveOnly The new state of the showActiveOnly Checkbox
     */
    private handleActiveChange(showActiveOnly: boolean) {
        this.processData(
            undefined,
            showActiveOnly,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
        );
    }

    /**
     * Calculates the number of pages and adjusts the current page if needed
     * @param items The filtered subset of data
     * @param itemsPerPage The number of items displayed on the page
     * @param currentPage The current page
     * @returns An object with the number of pages and a validated current page
     */
    private getUpdatedPaging(
        items: T[],
        itemsPerPage = this.state.itemsPerPage,
        currentPage = this.state.currentPage
    ) {
        const numPages = this.calculateNumberOfPages(items, itemsPerPage);

        return {
            numPages,
            currentPage:
                this.state.currentPage <= numPages ? currentPage : numPages
        };
    }

    /**
     * Handles the number of items per page changing
     * @param itemsPerPage New number of items to display per page
     */
    private handleItemsPerPageChange(itemsPerPage: number) {
        this.processData(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            itemsPerPage,
            undefined
        );
    }

    /**
     * Handles page changes
     * @param page New page
     */
    private changePage(page: number) {
        const currentPage =
            !isNaN(page) && page >= 1 && page <= this.state.numPages
                ? page
                : this.state.currentPage;

        this.processData(
            undefined,
            undefined,
            undefined,
            currentPage,
            undefined,
            undefined,
            undefined
        );
    }

    /**
     * Calculates the values to display in the page status (Displaying x to y of n...)
     * @param itemsPerPage The number of items displayed per page
     * @param currentPage The current page
     * @param numItems The total number of items in the filtered data set
     */
    private calculateCountDisplay(
        itemsPerPage: number,
        currentPage: number,
        numItems: number
    ): CountDisplayProps {
        const firstAndLastRows = this.calculateFirstAndLastRows(
            itemsPerPage,
            currentPage
        );

        const start = firstAndLastRows.first + (numItems === 0 ? 0 : 1);
        const end =
            firstAndLastRows.last <= numItems
                ? firstAndLastRows.last
                : numItems;
        const total = numItems;

        return {
            start,
            end,
            total
        };
    }

    /**
     * Calculates the first and last rows of the items that
     * should be displayed on the current page
     * @param itemsPerPage The number of items displayed per page
     * @param currentPage The current page
     */
    private calculateFirstAndLastRows(
        itemsPerPage: number,
        currentPage: number
    ) {
        const first = itemsPerPage * (currentPage - 1);
        const last = first + itemsPerPage;

        return {
            first,
            last
        };
    }

    /**
     * Gets the subset of items to display on the current page
     * @param filteredItems The filtered subset of data
     * @param itemsPerPage The number of items to display per page
     * @param currentPage The current page
     * @param numPages The total number of pages
     */
    private getItemsForCurrentPage(
        filteredItems: T[],
        itemsPerPage: number,
        currentPage: number,
        numPages: number
    ): T[] {
        const countInfo = this.calculateCountDisplay(
            itemsPerPage,
            currentPage,
            filteredItems.length
        );

        if (currentPage === numPages) {
            return filteredItems.slice(countInfo.start - 1);
        } else {
            return filteredItems.slice(countInfo.start - 1, countInfo.end);
        }
    }

    /**
     * Calculate the number of pages given the filtered subset of data and the number of items per page
     * @param filteredItems The subset of filtered items
     * @param itemsPerPage The number of items to display per page
     */
    private calculateNumberOfPages(filteredItems: T[], itemsPerPage: number) {
        const numPages =
            Math.floor(filteredItems.length / itemsPerPage) +
            (filteredItems.length % itemsPerPage === 0 ? 0 : 1);

        return numPages === 0 ? 1 : numPages;
    }

    /**
     * Filters the data using the filter text and the show active checkbox
     * @param filterText The filter text
     * @param showActiveOnly If we're only showing active recards
     * @param data The data
     */
    private filterData(filterText: string, showActiveOnly: boolean, data: T[]) {
        const filteredData = data
            .filter(item => !showActiveOnly || item.active)
            .filter(item => {
                if (!this.props.filterColumn) {
                    return true;
                }

                const key = this.props.filterColumn.key;
                const filterCell: any = (item as any)[key];
                const stringValue: string = String(filterCell);
                return stringValue.toLowerCase().indexOf(filterText.toLowerCase()) > -1;
            });

        return filteredData;
    }

    /**
     * Handles a change in sorting column or direction
     * @param sortColumn The column to sort on
     * @param sortDirection The direction to sort
     */
    private handleSort(sortColumn: Column, sortDirection: SortDirection) {
        this.processData(
            undefined,
            undefined,
            undefined,
            undefined,
            sortDirection,
            undefined,
            sortColumn
        );
    }

    /**
     * Sorts the given data
     * @param items The items to sort
     * @param sortDirection The direction to sort
     * @param sortColumn The column to sort on
     */
    private sortData(
        items: T[],
        sortDirection: SortDirection,
        sortColumn: Column
    ) {
        return items.sort((a, b) => {
            const key = sortColumn.key;

            if (a.hasOwnProperty(key) && b.hasOwnProperty(key)) {
                const first = a[key];
                const second = b[key];

                if (sortDirection === 'ASCENDING') {
                    if (first < second) {
                        return -1;
                    } else if (first > second) {
                        return 1;
                    } else {
                        return 0;
                    }
                } else {
                    if (first > second) {
                        return -1;
                    } else if (first < second) {
                        return 1;
                    } else {
                        return 0;
                    }
                }
            } else {
                return 0;
            }
        });
    }

    /**
     * Handles a change of an input on an input column
     * @param itemKey The key of the item that changes
     * @param column The column where the change ocurred
     * @param value The new value
     */
    private handleInputColumnChange(
        itemKey: string,
        column: Column,
        value: string
    ) {
        const data = [...this.state.data];

        for (const item of data) {
            if (item.key === itemKey) {
                item[column.key] = value;
            }
        }

        if (this.props.screenStateFunction) {
            this.props.screenStateFunction(true);
        }

        this.processData(data);
    }

    /**
     * Toggles the display of the fill popup
     * @param show True to show the fill popup
     */
    private toggleFillPopup(show: boolean) {
        const fillProps = this.state.fillProps;
        fillProps.show = show;
        return fillProps;
    }

    /**
     * Show the fill popup
     * @param column The column we are showing the fill popup for
     * @param event The event that triggered (from clicking the fill link)
     */
    private showFillPopup(
        column: Column,
        event: React.MouseEvent<HTMLAnchorElement>
    ) {
        this.setState({
            fillProps: {
                show: true,
                x: event.clientX,
                y: event.clientY
            },
            fillColumn: column
        });
    }

    /**
     * Fills fields with the given value, called from the Fill Popup
     * @param fillAll True to change all items, False to fill only visibile
     * @param value The value to fill
     */
    private onFill(fillAll: boolean, value?: string) {
        if (typeof value === 'undefined' || !this.state.fillColumn) {
            this.setState({ fillProps: this.toggleFillPopup(false) });
            return;
        }

        const data = [...this.state.data];
        const visible = [...this.state.displayedData];

        for (const item of data) {
            if (fillAll || visible.indexOf(item) > -1) {
                item[this.state.fillColumn.key] = value;
            }
        }

        this.processData(
            data,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            this.toggleFillPopup(false)
        );

        if (this.props.screenStateFunction) {
            this.props.screenStateFunction(true);
        }
    }

    /**
     * Main function for processing data
     * Handles filtering, sorting, paging, and updating state
     * @param data The data to process
     * @param showActiveOnly True to only show active records
     * @param filterText The filter text
     * @param currentPage The current page
     * @param sortDirection The sort direction
     * @param itemsPerPage Number of items per page
     * @param sortColumn The column to sort on
     * @param fillProps The properties for the fill popup (pass-through so we only update state once)
     */
    private processData(
        data = this.state.data,
        showActiveOnly: boolean = this.state.showActiveOnly,
        filterText: string = this.state.filterText,
        currentPage = this.state.currentPage,
        sortDirection = this.state.sortDirection,
        itemsPerPage = this.state.itemsPerPage,
        sortColumn = this.state.sortColumn,
        fillProps = this.state.fillProps
    ) {
        const temp = this.filterData(filterText, showActiveOnly, data);

        const filteredData = sortColumn
            ? this.sortData(temp, sortDirection, sortColumn)
            : temp;

        const updatedPaging = this.getUpdatedPaging(
            filteredData,
            itemsPerPage,
            currentPage
        );

        const displayedData = this.getItemsForCurrentPage(
            filteredData,
            itemsPerPage,
            updatedPaging.currentPage,
            updatedPaging.numPages
        );

        this.setState({
            data,
            showActiveOnly,
            filterText,
            currentPage,
            sortColumn,
            sortDirection,
            filteredData,
            displayedData,
            ...updatedPaging,
            fillProps,
            itemsPerPage
        });
    }
}

/**
 * Props common to all components
 */
export interface ComponentProps {
    idSuffix: string;
}

export type SortDirection = 'ASCENDING' | 'DESCENDING';

export type ColumnType = 'SIMPLE' | 'INPUT' | 'LINK';

/**
 * A column in the table
 */
export interface Column {
    /**
     * The column's title to display
     */
    title: string;

    /**
     * The object key to retrieve this column's data using
     */
    key: string;

    /**
     * The type of column this is
     */
    type: ColumnType;

    /**
     * True if the column should be sortable
     */
    sortable: boolean;

    /**
     * Optional function that will format/replace the display value
     */
    displayOverride?: (value: any) => string;

    /**
     * Function to execute when the link is clicked
     * Column Type LINK only
     */
    linkCallback?: (key: string) => void;
}

/**
 * The data passed to the table should implement this or an extending interface
 */
export interface DataTableData extends Object {
    /**
     * Record's isActive flag
     */
    active?: boolean;
    /**
     * Record's encrypted key
     */
    key: string;

    [key: string]: any;
}

export interface DataTableOptions<T> {
    /**
     * The data to display in the table
     */
    data: T[];

    /**
     * The columns to be displayed
     */
    columns: Column[];

    /**
     * Optional column to initially sort the data on
     */
    initialSortColumn?: Column;

    /**
     * Optional initial sort direction
     * Default is Ascending
     */
    initialSortDirection?: SortDirection;

    /**
     * Optional reference to a setScreenState function to call
     * when an input is modified
     */
    screenStateFunction?: (setIsModified: boolean) => void;

    /**
     * Optional column to provide a filter input for
     */
    filterColumn?: Column;

    /**
     * Default value of the Show n entries field
     * Default is 10
     */
    itemsPerPage?: number;

    /**
     * True to allow filtering by active flag
     */
    activeFiltering: boolean;

    /**
     * True to display in compact mode
     * No filter, only one compact control bar
     */
    compactMode?: boolean;
}

export interface CommonDataTableProps<T extends DataTableData> {
    /**
     * The id suffix
     */
    idSuffix: string;
    /**
     * Any styles to apply
     */
    style: React.CSSProperties;
    /**
     * The list of columns
     */
    columns: Column[];
    /**
     * The column being sorted on
     */
    sortColumn?: Column;
    /**
     * The sort handler
     */
    handleSort: (sortColumn: Column, sortDirection: SortDirection) => void;
    /**
     * Handler for when an input in an INPUT column changes
     */
    handleInputColumnChange: (
        itemKey: string,
        column: Column,
        value: string
    ) => void;
    /**
     * Function that displays the Fill popup
     */
    showFillPopup: (
        column: Column,
        event: React.MouseEvent<HTMLAnchorElement>
    ) => void;
    /**
     * Direction we're sorting
     */
    sortDirection: SortDirection;
    /**
     * Subset of data currently being displayed
     */
    displayedData: T[];
    /**
     * Props for the Fill popup
     */
    fillProps: FillPopupProps;
}
